import { useRecoilState, useResetRecoilState } from 'recoil';
import { drawerAtom } from './atoms';
import { DrawerComponentType } from './types';
import { useConfirmationModal } from 'services/modals/hooks';

type Params = {
  drawerContentType: DrawerComponentType;
  drawerComponentProps?: any;
  onClose?: () => void;
  preventClose?: boolean;
};

export const useDrawer = () => {
  const [drawer, setDrawer] = useRecoilState(drawerAtom);
  const resetState = useResetRecoilState(drawerAtom);
  const { showModal } = useConfirmationModal();

  const openDrawer = (params?: Params) => {
    const { drawerComponentProps, drawerContentType, onClose } = params || {};

    setDrawer((prev) => ({
      ...prev,
      isOpen: true,
      drawerComponentProps,
      drawerContentType,
      onClose: onClose
    }));
  };

  const closeDrawer = () => {
    const { onClose, preventClose, confirmationModalProps } = drawer;

    if (preventClose) {
      showModal({
        title: confirmationModalProps?.title || 'Close Drawer',
        message: confirmationModalProps?.message,
        confirmationText:
          confirmationModalProps?.confirmationText || 'Yes, close',
        cancelText: confirmationModalProps?.cancelText || 'No, keep it open',
        showSecondaryButton: !!confirmationModalProps?.secondaryText,
        secondaryText: confirmationModalProps?.secondaryText,
        width: 500,
        onConfirm: async () => {
          await confirmationModalProps?.onConfirm();
          onClose?.();
          resetState();
        },
        onSecondaryClick: () => {
          onClose?.();
          resetState();
        },
        onClose: () => {
          if (!confirmationModalProps?.secondaryText) {
            onClose?.();
            resetState();
          }
        }
      });
    } else {
      resetState();
      onClose?.();
    }
  };

  const forceCloseDrawer = () => {
    resetState();
  };

  const updateDrawerComponentProps = (
    drawerComponentProps: Record<string, any>
  ) => {
    setDrawer((prev) => ({
      ...prev,
      drawerComponentProps: {
        ...prev.drawerComponentProps,
        ...drawerComponentProps
      }
    }));
  };

  const updateDrawerProps = (drawerProps: {
    preventClose: boolean;
    confirmationModalProps?: {
      title: string;
      message?: string;
      onConfirm: () => void;
      confirmationText?: string;
      secondaryText?: string;
      cancelText?: string;
    };
  }) => {
    setDrawer((prev) => ({
      ...prev,
      ...drawerProps
    }));
  };

  return {
    ...drawer,
    closeDrawer,
    openDrawer,
    forceCloseDrawer,
    updateDrawerComponentProps,
    updateDrawerProps
  };
};
